import { Component, OnInit, ViewChild, ElementRef, OnDestroy, HostListener } from '@angular/core';
import { Location, DatePipe } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  InspectionService,
  MachineFamillyService,
  InspectionPluginService,
  LanguageService,
  PeopleService,
  LoadSpinnerService,
  InspectionSectionService,
  InspectionPointService,
  ApplicationService,
  MultiEditService,
  DataService,
  SessionDetectService
} from '../../shared/services';
import {
  Inspection,
  InspectionPlugin,
  InspectionPoint,
  InspectionSection,
  InspectionSession,
  InspectionWorkflow,
  Person
} from '../../shared/models';
import { BasePageComponent } from '../../base-page.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { OrderSummariesComponent } from '../../utils/component/order-summaries.component';
import { InputLangComponent } from '../../modal/inputlang.component';
import { PluginModelService } from '../../shared/services/pluginModel.service';
import { ApplicationType, ResourceType, SelectorComponent } from '../../utils/index';
import { ModalComponent } from '../../modal/modal.component';

import * as _ from 'lodash';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { CurrencyService } from '../../shared/services/currency.service';
import { InspectionValidator } from '../../shared/models/inspectionValidator.model';
import { InspectionDetailMenu, InspectionMenuComponent } from '../../menus/inspection.menu';
import { OfflineService } from '../../shared/services/offline-mode.service';
import { InformationComponent } from '../../modal/information.component';
import { catchError, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { PluginComponent } from '../../plugins/plugin.component';
import { timer, of, zip } from 'rxjs';
import { ConfirmWithOptionsComponent } from '../../modal/confirm-with-options.component';
import { InspectionClipboardService } from '../../shared/services/inspection-clipboard.service';
import { InspectionPartsManagementModalComponent } from '../../shared/components';
import { ObsolescencesPluginService, UpgradesPluginService } from '../../plugins';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  templateUrl: './detail.component.html',
  providers: [DatePipe],
  standalone: false
})
export class InspectionDetailPageComponent extends BasePageComponent implements OnInit, OnDestroy {
  inspection: Inspection;
  offlineInspection: Inspection;
  errorMessage: string;
  inspectionId: string;
  @ViewChild('fileInput', { static: true })
  fileInput: ElementRef;
  @ViewChild('pricesInput', { static: true })
  pricesInput: ElementRef;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('offlineRemoveConfirm', { static: true })
  offlineRemoveConfirm: ConfirmComponent;
  @ViewChild('sendToValidateConfirm', { static: true })
  sendToValidateConfirm: ConfirmComponent;
  @ViewChild('orderSections', { static: true })
  orderSectionsModal: OrderSummariesComponent;
  @ViewChild('orderPlugins', { static: true })
  orderPluginsModal: OrderSummariesComponent;
  @ViewChild('clipboardPasteReplaceConfirm', { static: true })
  clipboardPasteReplaceConfirm: ConfirmWithOptionsComponent;
  plugins: any[];
  @ViewChild('languagesModal', { static: true }) languageModal: InputLangComponent;
  @ViewChild('reportLangModal', { static: true }) reportLangModal: ModalComponent;
  @ViewChild('inspectionCopy', { static: true }) inspectionCopy: InformationComponent;
  @ViewChild('availableOfflineConfirm', { static: true }) availableOfflineConfirm: InformationComponent;
  @ViewChild('synchronizationModal', { static: true }) synchronizationModal: InformationComponent;
  @ViewChild('synchronizationErrorModal', { static: true }) synchronizationErrorModal: InformationComponent;
  @ViewChild('synchronizationCompleted', { static: true }) synchronizationCompleted: ConfirmComponent;
  @ViewChild('sectionsDeleteConfirm', { static: true }) sectionsDeleteConfirm: ConfirmComponent;
  @ViewChild('statusUpdateConfirm', { static: true }) statusUpdateConfirm: ConfirmComponent;
  @ViewChild('updateFromMachineModuleConfirm', { static: true }) updateFromMachineModuleConfirm: ConfirmComponent;
  @ViewChild('updateMachineModuleConfirm', { static: true }) updateMachineModuleConfirm: ConfirmComponent;
  @ViewChild('partsModal', { static: true }) partsModal: ModalComponent;
  @ViewChild('sapSynchronizationConfirm', { static: true }) sapSynchronizationConfirm: ConfirmComponent;
  @ViewChild('priceSynchronizationErrorModal', { static: true }) priceSynchronizationErrorModal: InformationComponent;

  loading = false;
  languages: any[];
  inspectionCopyMessage: string;
  inspectionRemoveMessage: string;
  loadedData: any;
  syncData: any;
  offlinePerson: Person;
  private alive: boolean;
  validatedPlugins: Array<{ data: InspectionPlugin; messages: string[] }> = [];
  selectedItems: { id: string; name: string }[] = [];
  inspectionWorkflow: { [key: number]: InspectionWorkflow };
  sapPricesAvailable = false;
  sapCurrency: string;

  constructor(
    public inspectionService: InspectionService,
    public pointService: InspectionPointService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    location: Location,
    private machineFamillyService: MachineFamillyService,
    private currencyService: CurrencyService,
    public snackBar: SnackBarService,
    private pluginsService: InspectionPluginService,
    private language: LanguageService,
    private offline: OfflineService,
    private peopleService: PeopleService,
    private sectionService: InspectionSectionService,
    private pluginModelService: PluginModelService,
    private loadSpinner: LoadSpinnerService,
    private clipboard: InspectionClipboardService,
    private upgradesPluginService: UpgradesPluginService,
    private obsolescencesPluginService: ObsolescencesPluginService,
    private applicationService: ApplicationService,
    private multiEditService: MultiEditService,
    private dataService: DataService,
    private sessionDetect: SessionDetectService
  ) {
    super(location);
    this.alive = true;
  }

  ngOnInit(): void {
    this.initRoute();
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) {
        this.router.navigate([this.inspectionsRoute]);
      }

      this.inspectionId = id;
      this.initData();
    });
  }

  openSection(section: InspectionSection): void {
    this.router.navigate([this.inspectionsRoute, this.inspection.id, 'sections', section.id]);
  }

  selectSection(section: any): void {
    section.selected = !section.selected;
    if (section.selected) {
      this.selectedItems.push({ id: section.id, name: section.name });
    } else {
      this.selectedItems = this.selectedItems.filter((s) => s.id !== section.id);
    }
  }

  initData(): void {
    const isInspectionWasLoaded = this.dataService.getInspection(this.inspectionId)?.isLoaded;
    zip(
      this.inspectionService.getInspection(this.inspectionId),
      this.peopleService.getCurrent(),
      this.applicationService.getInspectionWorkflow()
    )
      .flatMap(([el, person, workflow]) => {
        this.inspection = el;
        this.inspectionWorkflow = workflow;
        if (this.inspection.isNew) this.gotoEditPage();
        this.selectedItems.push(
          ...this.inspection.sections.filter((p: any) => p.selected).map((p: InspectionSection) => ({ id: p.id, name: p.name }))
        );
        this.currentPerson = person;
        this.checkSapPrices();
        if (!this.inspection.isOffline && !this.inspection.isSynchronizing)
          this.multiEditService.activate(this.inspectionId, this.inspection.etag, isInspectionWasLoaded);
        return this.pluginModelService.getPluginModelsValidationSchemas(el.inspectionType.toString(), el.status.toString()).pipe(
          tap((validationSchemas) => {
            const validator = new InspectionValidator(validationSchemas);
            this.inspection.accept(validator);
            if (validator.hasErrors()) {
              this.errorMessage = 'error';
              this.validatedPlugins = validator.errors;
            }
          })
        );
      })
      .flatMap(() => {
        if (this.inspection.offlinePersonId && this.inspection.offlinePersonId !== this.currentPerson.id) {
          return this.peopleService.getPerson(this.inspection.offlinePersonId).pipe(
            tap((offlinePerson) => {
              this.offlinePerson = offlinePerson;
            })
          );
        }
        return of(null);
      })
      .flatMap(() => {
        if (this.inspection.isSynchronizing) {
          return timer(0, 5000).pipe(
            takeWhile(() => this.alive),
            switchMap(() => this.offline.checkSynchronization(this.inspectionId)),
            tap((res) => {
              if (!res) this.refreshPage();
            })
          );
        }
        return of(null);
      })
      .subscribe();

    this.language.getLanguages().subscribe((data) => {
      this.languages = data;
    });

    if (
      this.offline.someInspectionOffline &&
      this.offline.getOfflineInspectionId !== this.inspectionId &&
      this.offlineInspectionAppType === this.currentAppType
    ) {
      this.inspectionService.getInspection(this.offline.getOfflineInspectionId).subscribe((inspection) => {
        this.offlineInspection = inspection;
      });
    }
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(InspectionMenuComponent, {
      inspection: this.inspection,
      currentPerson: this.currentPerson,
      anyModules: !!this.selectedItems.length
    });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 67) {
      if (this.clipboard.canProcess(this.inspection)) {
        this.copy();
      }
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 88) {
      if (this.clipboard.canProcess(this.inspection)) {
        this.cut();
      }
    }
    if (($event.ctrlKey || $event.metaKey) && $event.keyCode === 86) {
      if (this.clipboard.canProcess(this.inspection)) {
        this.paste();
      }
    }
  }

  public selectMenu(item: any) {
    switch (item) {
      case InspectionDetailMenu.Information:
        this.gotoInformationPage();
        break;
      case InspectionDetailMenu.Properties:
        this.gotoEditPage();
        break;
      case InspectionDetailMenu.ImportModule:
        this.gotoAddSectionPage();
        break;
      case InspectionDetailMenu.AddPlugin:
        this.gotoAddPluginPage();
        break;
      case InspectionDetailMenu.CreateModule:
        this.gotoCreateSectionPage();
        break;
      case InspectionDetailMenu.ImportParts:
        this.openFileSelector();
        break;
      case InspectionDetailMenu.Delete:
        this.openDeleteConfirm();
        break;
      case InspectionDetailMenu.SendToValidate:
        this.openSendToValidateConfirm();
        break;
      case InspectionDetailMenu.OrderModules:
        this.orderItems();
        break;
      case InspectionDetailMenu.DownloadReport:
        this.downloadReport();
        break;
      case InspectionDetailMenu.ReportList:
        this.gotoReportList();
        break;
      case InspectionDetailMenu.Collaborate:
        this.gotoCollaborate();
        break;
      case InspectionDetailMenu.OrderPlugins:
        this.openOrderPluginsPage();
        break;
      case InspectionDetailMenu.ChangeInputLanguage:
        this.openLanguagesSelector();
        break;
      case InspectionDetailMenu.OpenActionPlan:
        this.gotoActionPlan();
        break;
      case InspectionDetailMenu.AvailableOffline:
        this.makeAvailableOffline();
        break;
      case InspectionDetailMenu.RemoveFromOffline:
        this.openOfflineRemove();
        break;
      case InspectionDetailMenu.Synchronize:
        this.synchronize();
        break;
      case InspectionDetailMenu.TrashCan:
        this.gotoRestorePage();
        break;
      case InspectionDetailMenu.EditJson:
        this.gotoEditJsonPage();
        break;
      case InspectionDetailMenu.CopySelectedModules:
        this.copy();
        break;
      case InspectionDetailMenu.CutSelectedModules:
        this.cut();
        break;
      case InspectionDetailMenu.DeleteSelectedModules:
        this.openSectionsDeleteConfirm();
        break;
      case InspectionDetailMenu.PastModules:
        this.paste();
        break;
      case InspectionDetailMenu.ImportPartPrices:
        this.openPricesSelector();
        break;
      case InspectionDetailMenu.OpenMachines:
        this.gotoMachines();
        break;
      case InspectionDetailMenu.ManageParts:
        this.openPartsManagement();
        break;
      case InspectionDetailMenu.DownloadTranslation:
        this.downloadTranslation();
        break;
      case InspectionDetailMenu.Status:
        this.gotoUpdateStatusPage();
        break;
      default:
    }
  }

  openSectionsDeleteConfirm() {
    if (this.selectedItems.length > 0) {
      this.sectionsDeleteConfirm.open();
    }
  }

  public cut() {
    if (this.selectedItems.length > 0) {
      this.snackBar.success('snack-bar.menu.added-to-clipboard');
    } else {
      this.snackBar.warn('snack-bar.menu.nothing-selected');
    }
    const clipboard: any = {};
    clipboard.sourceInspectionId = this.inspectionId;
    clipboard.selectedItems = _.uniqBy(this.selectedItems, 'id');
    clipboard.sourceIsOffline = this.inspection.isOffline;
    clipboard.deleteSource = true;
    this.clipboard.setData(clipboard, this.inspection);
  }

  public copy() {
    if (this.selectedItems.length > 0) {
      this.snackBar.success('snack-bar.menu.added-to-clipboard');
    } else {
      this.snackBar.warn('snack-bar.menu.nothing-selected');
    }
    const clipboard: any = {};
    clipboard.sourceInspectionId = this.inspectionId;
    clipboard.selectedItems = _.uniqBy(this.selectedItems, 'id');
    clipboard.sourceIsOffline = this.inspection.isOffline;
    clipboard.deleteSource = false;
    this.clipboard.setData(clipboard, this.inspection);
  }

  public canPaste(): boolean {
    const clipboard: any = this.clipboard.getData(this.inspection);
    if (clipboard && clipboard.selectedItems && clipboard.selectedItems.length > 0) {
      if (clipboard.sourceIsOffline && this.inspection.isOffline) {
        return true;
      }
      if (this.isConnection && !clipboard.sourceIsOffline) {
        return true;
      }
    }
    return false;
  }

  public paste(replace?: boolean) {
    const clipboard: any = this.clipboard.getData(this.inspection);
    if (clipboard && clipboard.selectedItems && clipboard.selectedItems.length > 0) {
      const names: Array<string> = clipboard.selectedItems.map((p) => p.name);
      const intersectedItems = this.inspection.sections.filter((value) => names.includes(value.name));

      if (replace === undefined && !!intersectedItems.length) {
        this.clipboardPasteReplaceConfirm.open();
        return;
      }
      this.snackBar.success('snack-bar.menu.pasting');
      if (this.inspectionId) {
        this.inspectionService
          .copySection(
            this.inspectionId,
            clipboard.sourceInspectionId,
            clipboard.selectedItems.map((item) => item.id),
            clipboard.deleteSource,
            replace
          )
          .flatMap((result) => {
            if (result && replace && !!intersectedItems.length) {
              return this.sectionService.deleteSections(
                this.inspectionId,
                intersectedItems.map((x) => x.id)
              );
            }
            return of(result);
          })
          .subscribe(
            () => {
              this.clipboard.clearData(this.inspection);
              this.initData();
              this.inspection.sections.forEach((p: any) => (p.selected = false));
            },
            () => {
              this.snackBar.warn('snack-bar.something-went-wrong');
            }
          );
      }
    } else {
      this.snackBar.warn('snack-bar.menu.clipboard-is-empty');
    }
  }

  public deleteSections(e: any) {
    this.sectionService
      .deleteSections(
        this.inspectionId,
        this.inspection.sections.filter((p: any) => p.selected).map((x) => x.id)
      )
      .subscribe(() => {
        this.initData();
        this.selectedItems = [];
        this.inspection.sections.forEach((p: any) => (p.selected = false));
      });
  }

  public includeInOffline() {
    this.inspectionCopy.modalFooter = false;
    this.inspectionCopyMessage = 'offline.inspection-copy-modal.copy-message';
    this.inspectionCopy.open();
    this.inspectionCopy.loadSpinner = true;

    const subscribe = this.processMessage();
    this.loadSpinner.lockSpinner();
    this.inspectionService.changeOffline(this.inspectionId, { isOffline: true }).subscribe((res) => {
      this.offline.includeInspectionInOffline(this.inspectionId).subscribe((data) => {
        this.inspectionCopyMessage = 'offline.inspection-copy-modal.finish-message';
        this.inspectionCopy.modalFooter = true;
        this.inspectionCopy.loadSpinner = false;
        this.inspectionCopy.loadedIcon = true;
        this.loadSpinner.unlockSpinner();
        subscribe.unsubscribe();
        this.multiEditService.deactivate();
      });
    });
  }

  public makeAvailableOffline() {
    if (this.offline.someInspectionOffline) {
      if (!this.offlineInspection) {
        this.offlineInspection = <Inspection>{ name: this.offlineInspectionName };
      }
      this.availableOfflineConfirm.open();
    } else {
      this.includeInOffline();
    }
  }

  public removeFromOffline() {
    this.inspectionService.changeOffline(this.inspectionId, { isOffline: false }).subscribe((res) => {
      this.offline.removeInspectionFromOffline(this.inspectionId).subscribe((inspection) => {
        this.multiEditService.activate(this.inspectionId, this.inspection.etag, true);
      });
    });
  }

  public openOfflineRemove() {
    this.offlineRemoveConfirm.open();
  }

  public synchronize() {
    this.synchronizationModal.modalFooter = false;
    this.synchronizationModal.loadSpinner = true;
    this.synchronizationModal.open();
    this.loadSpinner.lockSpinner();
    this.offline.uploadData(this.inspectionId).subscribe((res) => {
      if (res) {
        this.synchronizationModal.dispose();
        this.synchronizationCompleted.open();
      } else {
        this.synchronizationModal.dispose();
        this.synchronizationErrorModal.open();
      }
      this.loadSpinner.unlockSpinner();
    });
    this.offline.getSyncCounter().subscribe((data) => {
      this.syncData = { value: data };
    });
  }

  continueLocally() {
    this.offline.continueLocal(this.inspectionId).subscribe((res) => {
      this.offline.synchronize(this.inspectionId).subscribe((s) => {
        if (s) {
          this.synchronizationCompleted.dispose();
        }
      });
    });
  }

  deleteLocal() {
    this.inspectionService.changeOffline(this.inspectionId, { isOffline: false }).subscribe((res) => {
      this.offline.removeInspectionFromOffline(this.inspectionId).subscribe((inspection) => {
        this.offline.synchronize(this.inspectionId).subscribe((s) => {
          if (s) {
            this.refresh();
          }
        });
      });
    });
  }

  public gotoActionPlan() {
    return this.router.navigate([this.actionPlansRoute, this.inspection.actionPlanId]);
  }

  public gotoMachines() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'machines']);
  }

  public gotoCollaborate() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'collaborate']);
  }

  public gotoReportList() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'reports']);
  }

  public gotoInformationPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'view']);
  }

  public gotoValidationSummaryPage() {
    if (this.validatedPlugins.length > 0) {
      return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'validations']);
    }
  }

  public gotoEditPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'edit']);
  }

  public gotoAddSectionPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'addsection']);
  }

  public gotoCreateSectionPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'sections', 'new']);
  }

  public gotoAddPluginPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'addPlugin', this.inspection.applicationSubType || '']);
  }

  public gotoRestorePage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'restore']);
  }

  public gotoEditJsonPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'editjson']);
  }

  public gotoUpdateStatusPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspection.id, 'status']);
  }

  public downloadTranslation() {
    this.language.downloadTranslationFiles([`inspection_${this.inspectionId}`]);
  }

  public openFileSelector() {
    this.fileInput.nativeElement.click();
  }

  public openPricesSelector() {
    this.pricesInput.nativeElement.click();
  }

  public openPartsManagement() {
    this.partsModal.hasClassModal = true;
    this.partsModal.open(InspectionPartsManagementModalComponent, { inspection: this.inspection });
  }

  public upload(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files && files.length > 0) {
      const inputFile = new FormData();
      for (let i = 0; i < files.length; i++) {
        inputFile.append(`files[${i}]`, files[i], files[i].name);
      }

      this.inspectionService.uploadParts(this.inspectionId, inputFile).subscribe((data) => {
        this.snackBar.success('snack-bar.success.parts-imported');
      });
    }
  }

  public uploadPartPrices(e: any) {
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files && files.length > 0) {
      const inputFile = new FormData();
      for (let i = 0; i < files.length; i++) {
        inputFile.append(`files[${i}]`, files[i], files[i].name);
      }

      this.inspectionService.uploadPartsPrices(this.inspectionId, inputFile).subscribe((data) => {
        this.snackBar.success('snack-bar.success.parts-imported');
      });
    }
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deleteInspection(e: any) {
    this.inspectionService.deleteInspection(this.inspectionId, this.inspection.actionPlanId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  openSendToValidateConfirm() {
    this.sendToValidateConfirm.open();
  }

  sendToValidate(e: any) {
    this.inspectionService.sendToValidate(this.inspectionId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  goToBack(): void {
    if (this.inspection.parentId && this.currentAppType === ApplicationType.Installation) {
      this.router.navigate([this.inspectionsRoute, this.inspection.parentId, 'inspections']);
      return;
    }

    InspectionService.machineSerial
      ? this.router.navigate([this.appRoute, 'machines', InspectionService.machineSerial, 'inspections'])
      : InspectionService.clientNumber
      ? this.router.navigate([this.appRoute, 'customers', InspectionService.clientNumber, 'inspections'])
      : this.router.navigate([this.inspectionsRoute]);
  }

  public orderItems() {
    const items = _.map(this.inspection.sections, (el: InspectionSection) => ({
      id: el.id,
      name: el.name,
      type: 'section',
      order: el.order
    }));

    this.orderSectionsModal.open(items);
  }

  saveSectionsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order
    }));

    this.inspectionService.orderItems(this.inspectionId, orderableItems).subscribe((el) => {});
  }

  public openOrderPluginsPage() {
    const orderableItems = _.map(this.inspection.plugins, (p: any) => ({
      id: p.id,
      name: this.language.getInstantValue(`plugins.${p.pluginType}.name`) || p.name || p.pluginType,
      type: 'plugin',
      order: p.order
    }));

    this.orderPluginsModal.open(orderableItems);
  }

  savePluginsOrder(items: any) {
    const orderableItems = _.map(items, (item: any) => ({
      id: item.id,
      order: item.order
    }));

    this.inspectionService.orderPlugins(this.inspectionId, orderableItems).subscribe((el) => {});
  }

  private downloadReport(): void {
    if (this.inspection.applicationType === ApplicationType.Maintenance && this.sapPricesAvailable) {
      this.sapSynchronizationConfirm.open();
    }
    this.openReportLangModal();
  }

  synchronizeSapAndOpenReport() {
    this.inspectionService.synchronizeSap(this.inspection.id, false).subscribe(() => {
      this.openReportLangModal();
    });
  }

  openReportLangModal() {
    const languageIndex = this.languages.findIndex((l) => l === this.inspection.inputLanguage);
    this.reportLangModal.modalFooter = false;
    this.reportLangModal.modalHeader = false;
    this.reportLangModal.modalMessage = false;
    this.reportLangModal.open(SelectorComponent, {
      values: this.languages.map((v) => v.toUpperCase()),
      selectedIndex: languageIndex
    });
  }

  public setReportLang(language: any) {
    (this.inspection.applicationType === ApplicationType.Maintenance ? this.checkUpgrades() : of({}))
      .pipe(switchMap(() => this.inspectionService.createReport(this.inspectionId, language.toLowerCase())))
      .subscribe(() => this.router.navigate([this.inspectionsRoute, this.inspection.id, 'reports']));
  }

  private checkUpgrades() {
    const upgradesPlugins = this.inspection.allPlugins.filter((p) => p.pluginType === 'upgrades');
    const obsolescencesPlugins = this.inspection.allPlugins.filter((p) => p.pluginType === 'obsolescences');
    this.pluginsService.updatePluginsPostUrl(this.inspection.id, [...upgradesPlugins, ...obsolescencesPlugins]);
    const upgradesObservable = upgradesPlugins.length
      ? this.upgradesPluginService.getUpgrades(this.inspection.id).pipe(
          switchMap((upgrades) => {
            const observables = [];
            upgrades.forEach((u) => {
              if (u.Status !== 'already-installed') {
                u.Recommended = true;
              }
            });

            upgradesPlugins.forEach((plugin) => {
              const oldLength = plugin.config.Upgrades.length;
              plugin.config.Upgrades = plugin.config.Upgrades.filter((upgrade) => upgrades.some((u) => u.Name === upgrade.Name));
              const newLength = plugin.config.Upgrades.length;
              const newUpgrades = upgrades.filter((upgrade) => !plugin.config.Upgrades.some((u) => u.Name === upgrade.Name));
              if (newUpgrades.length || oldLength !== newLength) {
                plugin.config.Upgrades.push(...newUpgrades);
                observables.push(
                  this.upgradesPluginService.updateUpgrades(this.inspection.id, plugin.id, plugin.config.Upgrades, {}, false)
                );
              }
            });
            return observables.length ? zip(...observables) : of(true);
          })
        )
      : of(true);
    const obsolescenceObservable = obsolescencesPlugins.length
      ? this.obsolescencesPluginService.getObsolescences(this.inspection.id).pipe(
          switchMap((obsolescences) => {
            const observables = [];
            obsolescences.forEach((obsolescence) => {
              if (obsolescence.Status !== 'already-installed') {
                obsolescence.Recommended = true;
              }
            });

            obsolescencesPlugins.forEach((plugin) => {
              const oldLength = plugin.config.Obsolescences.length;
              plugin.config.Obsolescences = plugin.config.Obsolescences.filter((obsolescence) =>
                obsolescences.some((o) => o.Name === obsolescence.Name)
              );
              const newLength = plugin.config.Obsolescences.length;
              const newObsolescences = obsolescences.filter(
                (obsolescence) => !plugin.config.Obsolescences.some((o) => o.Name === obsolescence.Name)
              );
              if (newObsolescences.length || oldLength !== newLength) {
                plugin.config.Obsolescences.push(...newObsolescences);
                observables.push(
                  this.obsolescencesPluginService.updateObsolescences(this.inspection.id, plugin.id, plugin.config.Obsolescences, {}, false)
                );
              }
            });
            return observables.length ? zip(...observables) : of(true);
          })
        )
      : of(true);

    return zip(upgradesObservable, obsolescenceObservable);
  }

  refreshPage() {
    this.refresh();
  }

  cancel() {}

  public openLanguagesSelector(): void {
    const languageIndex = this.languages.findIndex((l) => l === this.inspection.inputLanguage);
    this.languageModal.modalFooter = true;
    this.languageModal.modalHeader = true;
    this.languageModal.modalMessage = true;
    this.languageModal.open({
      values: this.languages,
      selectedIndex: languageIndex
    });
  }

  public selectLanguagesMenu($event): void {
    if (this.inspection.inputLanguage !== $event) {
      this.inspectionService.changeInputLanguage(this.inspectionId, $event).subscribe((r) => {
        if (r) {
          location.reload();
        }
      });
    }
  }

  updateStatus(value: number) {
    if (value > 0 && this.nextStatusFlow.status === 200 && this.inspection.getSerialNumber()) {
      this.updateFromMachineModuleConfirm.open();
    }

    if (value > 0 && this.nextStatusFlow.status === 350 && this.inspection.getSerialNumber()) {
      this.updateMachineModuleConfirm.open();
    }

    if (value < 0 || !this.nextStatusFlow?.confirmation) {
      this.inspectionService.updateStatus(this.inspection.id, Math.abs(value)).subscribe();
      return;
    }

    if (this.nextStatusFlow.confirmation) this.statusUpdateConfirm.open();
  }

  finishStatusUpdate() {
    (this.nextStatusFlow.prices && this.sapPricesAvailable
      ? this.inspectionService.synchronizeSap(this.inspection.id, false).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 422) {
              this.priceSynchronizationErrorModal.message = this.language.getInstantValue('inspections.parts.validate-parts-text');
              this.priceSynchronizationErrorModal.open();
            }
            if (error.status === 428) {
              this.priceSynchronizationErrorModal.message = this.language.getInstantValue('inspections.parts.wrong-customer-text');
              this.priceSynchronizationErrorModal.open();
            }
            throw error;
          }),
          switchMap((plugin) => {
            const invalidNumbers: string[] = plugin.config.Statuses?.filter((s) => s.Status === 'missing')?.map((s) => s.Number);
            if (invalidNumbers?.length) {
              this.priceSynchronizationErrorModal.message = `<div class="user-select-text">${this.language.getInstantValue(
                'inspections.parts.missing-parts',
                {
                  parts: invalidNumbers.join(', ')
                }
              )}</div>`;
              this.priceSynchronizationErrorModal.open();
              return this.priceSynchronizationErrorModal.confirmOutput.asObservable().pipe(map(() => true));
            }
            return of(true);
          })
        )
      : of(true)
    )
      .pipe(
        switchMap((result) => {
          if (!result) return of(false);
          return this.nextStatusFlow.status === 250 && this.inspection.progression !== 100
            ? this.pointService
                .updatePointsStatus(
                  this.inspection.id,
                  this.inspection.points.filter((p) => p.isDraft()).map((p) => p.id),
                  InspectionPoint.Done
                )
                .pipe(switchMap(() => this.inspectionService.updateStatus(this.inspection.id, 250)))
            : this.inspectionService.updateStatus(this.inspection.id, this.nextStatusFlow.status);
        })
      )
      .subscribe((updateStatusSuccess) => {
        if (!updateStatusSuccess) {
          this.snackBar.error('snack-bar.something-went-wrong');
          return;
        }
        if (this.currentStatusFlow.report && this.currentStatusFlow.status === 350) {
          this.router.navigate([this.inspectionsRoute, this.inspection.id, 'shares']);
          return;
        }

        if (this.currentStatusFlow.report) this.setReportLang(this.inspection.inputLanguage);

        if (this.currentStatusFlow.close) this.goToBack();
      });
  }

  navigateToSession(session: InspectionSession) {
    this.sessionDetect.navigate(session);
  }

  updateFromMachineModule() {
    this.inspectionService.updateFromMachineModule(this.inspection.id).subscribe((res) => this.refresh());
  }

  updateMachineModule() {
    this.inspectionService.updateMachineModule(this.inspection.id).subscribe();
  }

  private countResources(): number {
    let total = 3;
    const resources = [];
    this.inspection.allPlugins.forEach((plugin) => {
      if (PluginComponent.downloads[plugin.pluginType]) {
        const pluginResources = PluginComponent.downloads[plugin.pluginType](plugin);
        pluginResources.forEach((pr) => {
          if (!resources.some((r) => pr.resourceType === ResourceType.FormFile && pr.data.name === r.data.name)) {
            resources.push(pr);
          }
        });
      }
    });
    return total + resources.length;
  }

  private processMessage() {
    const total = this.countResources();
    this.loadedData = { value: `0/${total}` };
    let loaded = 0;
    return this.offline.getCounter().subscribe((data) => {
      loaded++;
      this.loadedData = { value: `${loaded}/${total}` };
    });
  }

  private checkSapPrices() {
    if (this.inspection.applicationType === ApplicationType.Maintenance)
      this.inspectionService.checkSapPrices(this.inspection.id).subscribe((res) => (this.sapPricesAvailable = res.available));
  }

  get isInspectionDone() {
    return this.inspection.status === 250;
  }

  get currentStatusFlow() {
    return this.inspectionWorkflow?.[this.inspection?.status];
  }

  get nextStatusFlow() {
    return this.inspectionWorkflow?.[this.currentStatusFlow?.next];
  }

  get updateStatusOkButton() {
    return this.nextStatusFlow?.status === 250 && this.inspection?.progression !== 100
      ? 'inspections.update-status.done-incomplete-ok'
      : 'inspections.update-status.update';
  }

  get updateStatusMessage() {
    if (!this.nextStatusFlow?.confirmation) return '';
    let text = this.language.getInstantValue(
      this.nextStatusFlow.status === 250 && this.inspection?.progression !== 100
        ? 'inspections.update-status.text-250-incomplete'
        : `inspections.update-status.text-${this.nextStatusFlow?.status}`,
      this.inspection
    );

    if (this.nextStatusFlow?.close) {
      text += this.language.getInstantValue('inspections.update-status.text-close');
    }

    if (this.nextStatusFlow?.prices) {
      text += this.language.getInstantValue(
        this.sapPricesAvailable ? 'inspections.update-status.text-with-prices' : 'inspections.update-status.text-without-prices'
      );
    }
    return text;
  }

  ngOnDestroy() {
    this.alive = false;
  }
}
